import { PLATFORM_API } from "./config";
import { freshSession, loadSession, saveSession, type Developer } from "./auth";
import type { ManagementPermission } from "./permissions";

export type { ManagementPermission } from "./permissions";

export type ApplicationStatus = "provisioning" | "active" | "disabled" | "error";
export type ApplicationType = "web" | "native";
export type CallbackEvent = "conversation.created" | "group.member.changed" | "message.sent" | "user.status.changed";

/** 可安全展示给开发者的控制面请求错误，不包含 Token、签名或响应正文。 */
export class PlatformRequestError extends Error {
  public constructor(
    message: string,
    public readonly status: number,
    public readonly code: string | null,
    public readonly requestId: string | null,
  ) {
    super(requestId === null ? message : `${message} · Request ID: ${requestId}`);
    this.name = "PlatformRequestError";
  }
}

/** 查询公开健康端点；失败时返回不可用状态，不要求开发者登录。 */
export async function getPlatformHealth(): Promise<"ok" | "unavailable"> {
  try {
    const response = await fetch(new URL("/health", PLATFORM_API), { cache: "no-store" });
    if (!response.ok) return "unavailable";
    const body = await response.json() as { readonly status?: unknown };
    return body.status === "ok" ? "ok" : "unavailable";
  } catch {
    return "unavailable";
  }
}
export type ApplicationCallbackType =
  | "MESSAGE_BEFORE_SEND" | "MESSAGE_AFTER_SEND" | "USER_ONLINE" | "USER_OFFLINE";

export interface ApplicationCallbackConfiguration {
  readonly id: string;
  readonly appId: string;
  readonly callbackType: ApplicationCallbackType;
  readonly callbackUrl: string;
  readonly enabled: boolean;
  readonly secretConfigured: boolean;
  readonly timeoutMs: number;
  readonly maxAttempts: number;
  readonly subscribedConversationTypes: readonly string[];
  readonly subscribedMessageTypes: readonly string[];
  readonly subscribedMessageSources: readonly string[];
  readonly status: "DRAFT" | "VERIFIED" | "ENABLED" | "DISABLED" | "ERROR";
  readonly lastVerifiedAt: number | null;
  readonly version: number;
  readonly createdAt: number;
  readonly updatedAt: number;
}

export interface ApplicationCallbackDraft {
  readonly callbackUrl: string;
  readonly timeoutMs: number;
  readonly maxAttempts: number;
  readonly subscribedConversationTypes: readonly string[];
  readonly subscribedMessageTypes: readonly string[];
  readonly subscribedMessageSources: readonly string[];
  readonly version?: number;
}

export type RetentionConversationType = "C2C" | "GROUP" | "ROOM" | "CHANNEL" | "BROADCAST";

export interface ApplicationRetentionPolicy {
  readonly appId: string;
  readonly policies: Readonly<Record<RetentionConversationType, { readonly historyDays: number }>>;
  readonly applyScope: "ALL_CONVERSATIONS";
  readonly status: "APPLYING" | "ACTIVE" | "FAILED";
  readonly effectiveAt: number;
  readonly lastError: string | null;
  readonly version: number;
  readonly updatedAt: number;
  readonly permissions: readonly ManagementPermission[];
}

export interface ApplicationRetentionPolicyDraft {
  readonly policies: ApplicationRetentionPolicy["policies"];
  readonly applyScope: "ALL_CONVERSATIONS";
  readonly effectiveAt: number;
}

export interface CallbackFailureLog {
  readonly id: string;
  readonly appId: string;
  readonly eventId: string;
  readonly eventType: string;
  readonly requestId: string;
  readonly messageId: string | null;
  readonly conversationId: string | null;
  readonly userId: string | null;
  readonly callbackUrl: string;
  readonly httpStatus: number | null;
  readonly errorType: string;
  readonly errorCode: string;
  readonly errorMessage: string;
  readonly responseSummary: string | null;
  readonly durationMs: number;
  readonly deliveryAttempt: number;
  readonly deliveryStatus: "RETRYING" | "RECOVERED" | "DEAD_LETTER" | "BLOCKED";
  readonly nextRetryAt: number | null;
  readonly firstFailedAt: number;
  readonly lastFailedAt: number;
  readonly recoveredAt: number | null;
  readonly createdAt: number;
  readonly expiresAt: number;
}

export interface PushConfiguration {
  readonly ios?: {
    readonly enabled: boolean;
    readonly bundleId?: string;
    readonly environment?: "development" | "production";
    readonly teamId?: string;
    readonly keyId?: string;
  };
  readonly android?: { readonly enabled: boolean; readonly projectId?: string };
}

export interface Application {
  readonly appId: string;
  readonly appKey: string;
  readonly tenantId: string;
  readonly clientId: string;
  readonly name: string;
  readonly description: string | null;
  readonly appType: ApplicationType;
  readonly redirectUris: readonly string[];
  readonly allowedOrigins: readonly string[];
  readonly adminUserId: string;
  readonly callbackUrl: string | null;
  readonly callbackEvents: readonly CallbackEvent[];
  readonly userSigExpireSeconds: number;
  readonly push: PushConfiguration;
  readonly credential: {
    readonly configured: boolean;
    readonly keyId: string | null;
    readonly version: number;
    readonly createdAt: number | null;
  };
  readonly enabled: boolean;
  readonly status: ApplicationStatus;
  readonly lastError: string | null;
  readonly version: number;
  readonly createdAt: number;
  readonly updatedAt: number;
  readonly permissions: readonly ManagementPermission[];
}

export interface PageResult<T> {
  readonly items: readonly T[];
  readonly pageNo: number;
  readonly pageSize: number;
  readonly total: number;
  readonly hasMore: boolean;
  readonly requestId: string;
}

export interface ImUserListItem {
  readonly userId: string;
  readonly displayName: string;
  readonly avatarUrl: string | null;
  readonly status: number;
  readonly source: "CLIENT_SSO" | "SERVER_API" | "UNKNOWN";
  readonly homePool: string;
  readonly createdAt: number;
  readonly lastActiveAt: number | null;
}

export interface ImGroupListItem {
  readonly groupId: string;
  readonly name: string;
  readonly groupType: "group" | "room" | "channel" | "broadcast";
  readonly ownerUserId: string | null;
  readonly memberCount: number;
  readonly status: number;
  readonly createdAt: number;
}

export interface ServerMessageListItem {
  readonly messageId: string;
  readonly requestId: string;
  readonly conversationId: string;
  readonly conversationType: string;
  readonly senderUserId: string;
  readonly senderType: "NORMAL_USER" | "SYSTEM_ACCOUNT";
  readonly receiverType: "user" | "group";
  readonly receiverId: string;
  readonly messageType: string;
  readonly contentSummary: string;
  readonly sendStatus: "accepted";
  readonly sendTime: number;
}

export interface ServerMessageDetail extends ServerMessageListItem {
  readonly traceId: string;
  readonly clientMsgId: string;
  readonly seq: number;
  readonly acceptedAt: number;
  readonly errorCode: string | null;
  readonly errorMessage: string | null;
  readonly rawContent: string;
  /** @deprecated The console renders rawContent. */
  readonly safeContent: Readonly<Record<string, unknown>>;
}

export type SystemAccountMessageType = "text" | "image" | "audio" | "video" | "file" | "custom" | "command";

export interface SystemAccount {
  readonly appId: string;
  readonly systemUserId: string;
  readonly displayName: string;
  readonly avatarUrl: string | null;
  readonly description: string;
  readonly status: "ENABLED" | "DISABLED";
  readonly isDefault: boolean;
  readonly replyPolicy: "DISABLED";
  readonly allowedMessageTypes: readonly SystemAccountMessageType[];
  readonly rateLimitPerSecond: number;
  readonly rateLimitPerDay: number;
  readonly homePool: string;
  readonly createdBy: string;
  readonly createdAt: number;
  readonly updatedBy: string;
  readonly updatedAt: number;
  readonly version: number;
  readonly sentToday: number;
}

export interface SystemAccountDraft {
  readonly systemUserId: string;
  readonly displayName: string;
  readonly avatarUrl: string | null;
  readonly description: string;
  readonly isDefault: boolean;
  readonly replyPolicy: "DISABLED";
  readonly allowedMessageTypes: readonly SystemAccountMessageType[];
  readonly rateLimitPerSecond: number;
  readonly rateLimitPerDay: number;
}

export type SystemAccountMessageTarget =
  | { readonly type: "USER"; readonly userId: string }
  | { readonly type: "GROUP"; readonly groupId: string };

export interface SystemAccountMessageDraft {
  readonly to: SystemAccountMessageTarget;
  readonly clientMsgId?: string;
  readonly message: {
    readonly type: Uppercase<SystemAccountMessageType>;
    readonly text?: string;
    readonly payload?: unknown;
  };
}

export interface SystemAccountBatchMessageDraft {
  readonly to: {
    readonly type: "GROUP_USERS";
    readonly groupId: string;
    readonly userIds: readonly string[];
  };
  readonly clientBatchId?: string;
  readonly message: SystemAccountMessageDraft["message"];
}

/** Online-only broadcast sent by a selected system account. */
export interface SystemAccountGlobalBroadcastDraft {
  readonly clientMsgId?: string;
  readonly message: SystemAccountMessageDraft["message"];
}

export interface SystemAccountMessageAck {
  readonly messageId: string;
  readonly clientMsgId: string;
  readonly conversationId: string;
  readonly seq: number;
  readonly sentAt: number;
  readonly requestId: string;
}

/** ACK after one non-durable global online fan-out attempt has completed. */
export interface SystemAccountGlobalBroadcastAck extends SystemAccountMessageAck {
  readonly activeShardCount: number;
  readonly deliveryScope: "ONLINE_GLOBAL";
}

export interface SystemAccountBatchMessageAck {
  readonly batchId: string;
  readonly groupId: string;
  readonly recipientCount: number;
  readonly messages: readonly (Omit<SystemAccountMessageAck, "requestId"> & {
    readonly recipientUserId: string;
  })[];
  readonly requestId: string;
}

export interface SystemAccountAudit {
  readonly auditId: string;
  readonly requestId: string;
  readonly systemUserId: string;
  readonly operatorId: string;
  readonly operation: "create" | "update" | "enable" | "disable" | "set_default";
  readonly before: unknown;
  readonly after: unknown;
  readonly ip: string | null;
  readonly userAgent: string | null;
  readonly createdAt: number;
}

export interface ImUserDraft {
  readonly userId: string;
  readonly displayName?: string;
  readonly avatarUrl?: string | null;
  readonly homePool?: string;
}

export interface ImGroupDraft {
  readonly groupId?: string;
  readonly name: string;
  readonly groupType: ImGroupListItem["groupType"];
  readonly ownerUserId: string;
  readonly initialMemberUserIds: readonly string[];
  readonly homePool?: string;
}

export interface CreatedImGroup {
  readonly groupId: string;
  readonly name: string;
  readonly groupType: ImGroupListItem["groupType"];
  readonly ownerUserId: string;
  readonly memberCount: number;
  readonly memberVersion: number;
}

export interface Endpoints {
  readonly issuer: string;
  readonly api: string;
  readonly webSocket: string;
  readonly restApi: string;
  readonly callbackJwks: string;
  readonly sdk: {
    readonly web: string;
    readonly typescript: string;
    readonly java: string;
    readonly flutter: string;
  };
}

export interface NativeCredentials {
  readonly appKey: string;
  readonly appSecret: string;
  readonly adminUserId: string;
  readonly restApiDomain: string;
  readonly userSigExpireSeconds: number;
  readonly keyId: string;
  readonly keyVersion: number;
}

export interface ApplicationDraft {
  readonly name: string;
  readonly description: string | null;
  readonly appType: ApplicationType;
  readonly redirectUris: readonly string[];
  readonly allowedOrigins: readonly string[];
  readonly adminUserId: string;
  readonly callbackUrl: string | null;
  readonly callbackEvents: readonly CallbackEvent[];
  readonly userSigExpireSeconds: number;
  readonly push: PushConfiguration;
}

export async function loadDeveloper(): Promise<Developer> {
  const response = await platformFetch("/v1/me");
  const value = await response.json() as { readonly user: Developer };
  const current = loadSession();
  if (current !== null) saveSession({ ...current, user: value.user });
  return value.user;
}

export async function listApplications(): Promise<readonly Application[]> {
  const response = await platformFetch("/v1/applications");
  return (await response.json() as { readonly applications: Application[] }).applications;
}

export async function getApplication(appId: string): Promise<{
  readonly application: Application;
  readonly endpoints: Endpoints;
}> {
  const response = await platformFetch(`/v1/applications/${encodeURIComponent(appId)}`);
  return response.json() as Promise<{ application: Application; endpoints: Endpoints }>;
}

export async function createApplication(draft: ApplicationDraft): Promise<{
  readonly application: Application;
  readonly endpoints: Endpoints;
  readonly credentials?: NativeCredentials;
}> {
  const response = await platformFetch("/v1/applications", {
    method: "POST",
    headers: { "idempotency-key": crypto.randomUUID() },
    body: JSON.stringify(draft),
  });
  return response.json() as Promise<{
    application: Application;
    endpoints: Endpoints;
    credentials?: NativeCredentials;
  }>;
}

export async function rotateApplicationCredentials(
  application: Application,
): Promise<{
  readonly application: Application;
  readonly endpoints: Endpoints;
  readonly credentials: NativeCredentials;
}> {
  const response = await platformFetch(
    `/v1/applications/${encodeURIComponent(application.appId)}/credentials/rotate`,
    { method: "POST", body: JSON.stringify({ version: application.version }) },
  );
  return response.json() as Promise<{
    application: Application;
    endpoints: Endpoints;
    credentials: NativeCredentials;
  }>;
}

export async function updateApplication(
  application: Application,
  patch: Partial<ApplicationDraft> & { readonly enabled?: boolean },
): Promise<{ readonly application: Application; readonly endpoints: Endpoints }> {
  const response = await platformFetch(`/v1/applications/${encodeURIComponent(application.appId)}`, {
    method: "PATCH",
    body: JSON.stringify({ ...patch, version: application.version }),
  });
  return response.json() as Promise<{ application: Application; endpoints: Endpoints }>;
}

/** Loads the versioned message archive policy for one owned application. */
export async function getApplicationRetentionPolicy(
  appId: string,
): Promise<ApplicationRetentionPolicy> {
  const response = await platformFetch(
    `/v1/applications/${encodeURIComponent(appId)}/retention-policy`,
  );
  return (await response.json() as {
    readonly retentionPolicy: ApplicationRetentionPolicy;
  }).retentionPolicy;
}

/** Saves a complete policy using the version returned by the preceding read. */
export async function updateApplicationRetentionPolicy(
  policy: ApplicationRetentionPolicy,
  draft: ApplicationRetentionPolicyDraft,
): Promise<ApplicationRetentionPolicy> {
  const response = await platformFetch(
    `/v1/applications/${encodeURIComponent(policy.appId)}/retention-policy`,
    {
      method: "PATCH",
      body: JSON.stringify({ ...draft, version: policy.version }),
    },
  );
  return (await response.json() as {
    readonly retentionPolicy: ApplicationRetentionPolicy;
  }).retentionPolicy;
}

export async function listImUsers(
  appId: string,
  query: { readonly pageNo: number; readonly pageSize: number; readonly keyword: string; readonly status: string },
): Promise<PageResult<ImUserListItem>> {
  return managementPage<ImUserListItem>(appId, "im-users", query);
}

export async function createImUser(
  appId: string,
  draft: ImUserDraft,
): Promise<{ readonly user: ImUserListItem; readonly idempotent: boolean; readonly requestId: string }> {
  const response = await platformFetch(managementPath(appId, "im-users"), {
    method: "POST",
    headers: { "idempotency-key": crypto.randomUUID() },
    body: JSON.stringify(draft),
  });
  return response.json() as Promise<{ user: ImUserListItem; idempotent: boolean; requestId: string }>;
}

export async function listImGroups(
  appId: string,
  query: { readonly pageNo: number; readonly pageSize: number; readonly keyword: string; readonly status: string; readonly groupType: string },
): Promise<PageResult<ImGroupListItem>> {
  return managementPage<ImGroupListItem>(appId, "im-groups", query);
}

export async function createImGroup(
  appId: string,
  draft: ImGroupDraft,
): Promise<{ readonly group: CreatedImGroup; readonly idempotent: boolean; readonly requestId: string }> {
  const response = await platformFetch(managementPath(appId, "im-groups"), {
    method: "POST",
    headers: { "idempotency-key": crypto.randomUUID() },
    body: JSON.stringify(draft),
  });
  return response.json() as Promise<{ group: CreatedImGroup; idempotent: boolean; requestId: string }>;
}

export async function listServerMessages(
  appId: string,
  query: { readonly pageNo: number; readonly pageSize: number; readonly keyword: string },
): Promise<PageResult<ServerMessageListItem>> {
  return managementPage<ServerMessageListItem>(appId, "server-messages", query);
}

export async function getServerMessage(
  appId: string,
  messageId: string,
): Promise<{ readonly message: ServerMessageDetail; readonly requestId: string }> {
  const response = await platformFetch(
    `${managementPath(appId, "server-messages")}/${encodeURIComponent(messageId)}`,
  );
  return response.json() as Promise<{ message: ServerMessageDetail; requestId: string }>;
}

export async function listSystemAccounts(
  appId: string,
  query: { readonly pageNo: number; readonly pageSize: number; readonly keyword: string; readonly status: string },
): Promise<PageResult<SystemAccount>> {
  return managementPage<SystemAccount>(appId, "system-accounts", query);
}

export async function createSystemAccount(
  appId: string,
  draft: SystemAccountDraft,
): Promise<{ readonly systemAccount: SystemAccount; readonly idempotent: boolean; readonly requestId: string }> {
  const response = await platformFetch(managementPath(appId, "system-accounts"), {
    method: "POST",
    headers: { "idempotency-key": crypto.randomUUID() },
    body: JSON.stringify(draft),
  });
  return response.json() as Promise<{ systemAccount: SystemAccount; idempotent: boolean; requestId: string }>;
}

export async function getSystemAccountDetail(
  appId: string,
  systemUserId: string,
): Promise<{ readonly systemAccount: SystemAccount; readonly audits: readonly SystemAccountAudit[]; readonly requestId: string }> {
  const response = await platformFetch(
    `${managementPath(appId, "system-accounts")}/${encodeURIComponent(systemUserId)}`,
  );
  return response.json() as Promise<{
    systemAccount: SystemAccount;
    audits: readonly SystemAccountAudit[];
    requestId: string;
  }>;
}

export async function updateSystemAccount(
  appId: string,
  account: SystemAccount,
  patch: Partial<Omit<SystemAccountDraft, "systemUserId">>,
): Promise<{ readonly systemAccount: SystemAccount; readonly requestId: string }> {
  const response = await platformFetch(
    `${managementPath(appId, "system-accounts")}/${encodeURIComponent(account.systemUserId)}`,
    { method: "PATCH", body: JSON.stringify({ ...patch, version: account.version }) },
  );
  return response.json() as Promise<{ systemAccount: SystemAccount; requestId: string }>;
}

export async function setSystemAccountEnabled(
  appId: string,
  account: SystemAccount,
  enabled: boolean,
): Promise<{ readonly systemAccount: SystemAccount; readonly requestId: string }> {
  const action = enabled ? "enable" : "disable";
  const response = await platformFetch(
    `${managementPath(appId, "system-accounts")}/${encodeURIComponent(account.systemUserId)}/${action}`,
    { method: "POST", body: JSON.stringify({ version: account.version }) },
  );
  return response.json() as Promise<{ systemAccount: SystemAccount; requestId: string }>;
}

/** Sends one direct or group message with an enabled application system account. */
export async function sendSystemAccountMessage(
  appId: string,
  systemUserId: string,
  draft: SystemAccountMessageDraft,
  idempotencyKey: string = crypto.randomUUID(),
): Promise<SystemAccountMessageAck> {
  const response = await platformFetch(
    `${managementPath(appId, "system-accounts")}/${encodeURIComponent(systemUserId)}/messages`,
    {
      method: "POST",
      headers: { "idempotency-key": idempotencyKey },
      body: JSON.stringify(draft),
    },
  );
  return response.json() as Promise<SystemAccountMessageAck>;
}

/** Sends one independently idempotent direct message to each selected active group member. */
export async function sendSystemAccountBatchMessage(
  appId: string,
  systemUserId: string,
  draft: SystemAccountBatchMessageDraft,
  idempotencyKey: string = crypto.randomUUID(),
): Promise<SystemAccountBatchMessageAck> {
  const response = await platformFetch(
    `${managementPath(appId, "system-accounts")}/${encodeURIComponent(systemUserId)}/batch-messages`,
    {
      method: "POST",
      headers: { "idempotency-key": idempotencyKey },
      body: JSON.stringify(draft),
    },
  );
  return response.json() as Promise<SystemAccountBatchMessageAck>;
}

/** Dispatches a non-durable broadcast to users connected when fan-out begins. */
export async function sendSystemAccountGlobalBroadcast(
  appId: string,
  systemUserId: string,
  draft: SystemAccountGlobalBroadcastDraft,
  idempotencyKey: string = crypto.randomUUID(),
): Promise<SystemAccountGlobalBroadcastAck> {
  const response = await platformFetch(
    `${managementPath(appId, "system-accounts")}/${encodeURIComponent(systemUserId)}/global-broadcast`,
    {
      method: "POST",
      headers: { "idempotency-key": idempotencyKey },
      body: JSON.stringify(draft),
    },
  );
  return response.json() as Promise<SystemAccountGlobalBroadcastAck>;
}

export async function listApplicationCallbacks(
  appId: string,
): Promise<readonly ApplicationCallbackConfiguration[]> {
  const response = await platformFetch(
    `/v1/applications/${encodeURIComponent(appId)}/callbacks`,
  );
  return (await response.json() as {
    readonly callbacks: readonly ApplicationCallbackConfiguration[];
  }).callbacks;
}

export async function saveApplicationCallback(
  appId: string,
  callbackType: ApplicationCallbackType,
  draft: ApplicationCallbackDraft,
): Promise<{
  readonly callback: ApplicationCallbackConfiguration;
  readonly secret?: string;
  readonly secretDisplayedOnce?: boolean;
}> {
  const response = await platformFetch(
    `/v1/applications/${encodeURIComponent(appId)}/callbacks/${callbackType}`,
    { method: "PUT", body: JSON.stringify(draft), cache: "no-store" },
  );
  return response.json() as Promise<{
    callback: ApplicationCallbackConfiguration;
    secret?: string;
    secretDisplayedOnce?: boolean;
  }>;
}

export async function executeApplicationCallbackAction(
  appId: string,
  callbackType: ApplicationCallbackType,
  action: "enable" | "disable" | "verify" | "rotate-secret",
): Promise<{
  readonly callback: ApplicationCallbackConfiguration;
  readonly secret?: string;
  readonly previousSecretValidUntil?: number;
}> {
  const response = await platformFetch(
    `/v1/applications/${encodeURIComponent(appId)}/callbacks/${callbackType}/${action}`,
    { method: "POST", body: JSON.stringify({}), cache: "no-store" },
  );
  return response.json() as Promise<{
    callback: ApplicationCallbackConfiguration;
    secret?: string;
    previousSecretValidUntil?: number;
  }>;
}

export interface ApplicationCallbackSecretMetadata {
  readonly appId: string;
  readonly configured: boolean;
  readonly version: number | null;
  readonly previousSecretValidUntil: number | null;
}

/** 受审计的应用级密钥响应；四类回调共用，仅供控制台临时展示。 */
export interface ApplicationCallbackSecret {
  readonly appId: string;
  readonly scope: "APPLICATION";
  readonly secret: string;
  readonly version: number;
  readonly requestId: string;
  readonly previousSecretValidUntil?: number | null;
}

/** Non-sensitive application-key state; callback configuration versions are unrelated. */
export async function getApplicationCallbackSecretMetadata(appId: string): Promise<ApplicationCallbackSecretMetadata> {
  const response = await platformFetch(`/v1/applications/${encodeURIComponent(appId)}/callback-secret`, { cache: "no-store" });
  const body = await response.json() as { readonly key?: unknown };
  if (typeof body.key !== "object" || body.key === null || Array.isArray(body.key)) {
    throw new Error("应用回调密钥状态无效");
  }
  const key = body.key as Record<string, unknown>;
  if (key.appId !== appId || typeof key.configured !== "boolean" ||
    (key.configured ? typeof key.version !== "number" || !Number.isInteger(key.version) || key.version < 1 : key.version !== null) ||
    (key.previousSecretValidUntil !== null && (typeof key.previousSecretValidUntil !== "number" || !Number.isFinite(key.previousSecretValidUntil)))) {
    throw new Error("应用回调密钥状态无效");
  }
  return { appId, configured: key.configured, version: key.version as number | null,
    previousSecretValidUntil: key.previousSecretValidUntil };
}

/** Explicit disclosure only: no browser cache, and no callback-type-specific secret. */
export async function readApplicationCallbackSecret(
  appId: string,
): Promise<ApplicationCallbackSecret> {
  const response = await platformFetch(
    `/v1/applications/${encodeURIComponent(appId)}/callback-secret/reveal`,
    { method: "POST", body: JSON.stringify({}), cache: "no-store" },
  );
  return parseApplicationCallbackSecret(response, appId);
}

/** Rotates the application key used by every configured callback without changing their settings. */
export async function rotateApplicationCallbackSecret(
  appId: string,
  version?: number,
): Promise<ApplicationCallbackSecret> {
  const response = await platformFetch(
    `/v1/applications/${encodeURIComponent(appId)}/callback-secret/rotate`,
    { method: "POST", body: JSON.stringify(version === undefined ? {} : { version }), cache: "no-store" },
  );
  return parseApplicationCallbackSecret(response, appId);
}

async function parseApplicationCallbackSecret(response: Response, appId: string): Promise<ApplicationCallbackSecret> {
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    throw new Error("回调密钥响应无效");
  }
  if (typeof body !== "object" || body === null || Array.isArray(body)) {
    throw new Error("回调密钥响应无效");
  }
  const value = body as Record<string, unknown>;
  if (value.appId !== appId || value.scope !== "APPLICATION" ||
    typeof value.secret !== "string" || !/^[A-Za-z0-9_-]{43}$/u.test(value.secret) ||
    typeof value.version !== "number" || !Number.isInteger(value.version) || value.version < 1 ||
    typeof value.requestId !== "string" || value.requestId.length === 0 ||
    (value.previousSecretValidUntil !== undefined && value.previousSecretValidUntil !== null &&
      (typeof value.previousSecretValidUntil !== "number" || !Number.isFinite(value.previousSecretValidUntil)))) {
    throw new Error("回调密钥响应无效");
  }
  return { appId, scope: "APPLICATION", secret: value.secret, version: value.version, requestId: value.requestId,
    ...(value.previousSecretValidUntil === undefined ? {} : { previousSecretValidUntil: value.previousSecretValidUntil }) };
}

export async function listCallbackFailureLogs(
  appId: string,
  query: Readonly<Record<string, string | number | undefined>>,
): Promise<{ readonly logs: readonly CallbackFailureLog[]; readonly nextCursor: string | null }> {
  const search = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== "") search.set(key, String(value));
  });
  const response = await platformFetch(
    `/v1/applications/${encodeURIComponent(appId)}/callback-failure-logs?${search.toString()}`,
  );
  return response.json() as Promise<{
    logs: readonly CallbackFailureLog[];
    nextCursor: string | null;
  }>;
}

export async function getCallbackFailureLog(
  appId: string,
  logId: string,
): Promise<CallbackFailureLog> {
  const response = await platformFetch(
    `/v1/applications/${encodeURIComponent(appId)}/callback-failure-logs/${encodeURIComponent(logId)}`,
  );
  return (await response.json() as { readonly log: CallbackFailureLog }).log;
}

async function managementPage<T>(
  appId: string,
  resource: "im-users" | "im-groups" | "server-messages" | "system-accounts",
  query: Readonly<Record<string, string | number>>,
): Promise<PageResult<T>> {
  const search = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== "") search.set(key, String(value));
  });
  const response = await platformFetch(`${managementPath(appId, resource)}?${search.toString()}`);
  return response.json() as Promise<PageResult<T>>;
}

function managementPath(
  appId: string,
  resource: "im-users" | "im-groups" | "server-messages" | "system-accounts",
): string {
  return `/open-platform/apps/${encodeURIComponent(appId)}/${resource}`;
}

async function platformFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const existing = loadSession();
  if (existing === null) throw new Error("请先登录");
  const session = await freshSession(existing);
  const headers = new Headers(init.headers);
  headers.set("authorization", `Bearer ${session.accessToken}`);
  if (init.body !== undefined) headers.set("content-type", "application/json");
  const response = await fetch(new URL(path, PLATFORM_API), { ...init, headers });
  if (response.status === 401) saveSession(null);
  if (!response.ok) {
    let message = `请求失败 (${response.status})`;
    let code: string | null = null;
    let requestId = response.headers.get("x-request-id");
    try {
      const body = await response.json() as {
        readonly error?: { readonly code?: string; readonly message?: string };
        readonly requestId?: string;
      };
      message = body.error?.message ?? message;
      code = body.error?.code ?? null;
      requestId = body.requestId ?? requestId;
    } catch {
      // Use the bounded generic message when the body is not JSON.
    }
    throw new PlatformRequestError(message, response.status, code, requestId);
  }
  return response;
}
